import '@/assets/carousel.css'
import { useHead } from '@unhead/vue'
import '@vuepic/vue-datepicker/dist/main.css'
import { useWindowSize } from '@vueuse/core'
import {
	Ref,
	computed,
	onBeforeUnmount,
	onMounted,
	reactive,
	ref,
	watch,
	watchEffect,
} from 'vue'
import { useRoute } from 'vue-router'
import { useToast } from 'vue-toastification'

import useInvalidateQuery from '@/composables/useInvalidateQueryData'
import useUserCredit from '@/composables/useUserCredit'

import useHotel from '@/modules/hotels/composables/useHotel'
import { useHotelImages } from '@/modules/hotels/composables/useHotelImages'
import { useHotelTexts } from '@/modules/hotels/composables/useHotelTexts'
import { CarouselModels } from '@/modules/hotels/interfaces/carouselModels'
import { Category, HotelImages } from '@/modules/hotels/interfaces/hotelImages'

import useHotels from '../composables/useHotels'
import { DetailsTab } from '../interfaces/detailsTab'
import { Item } from '../interfaces/hotelDetailsResponse'
import useRoomListFormatting from './useRoomListFormatting'

const useHotelDetails = () => {
	const route = useRoute()
	useHead({
		title: `${route.query.hotelName ?? 'Hotel details'}`,
	})
	const travelers = ref<number[]>(
		JSON.parse((route.query.users as string) ?? '[]')
	)
	const { hotel, isLoading, setTravelers, isError, isFetching } = useHotel()
	const { categories } = useHotelImages()
	const { getHotelTexts } = useHotelTexts()
	const hotelDetails = ref<{ hotelDetails: HTMLElement | null }>()
	const rate = ref()
	const photosAndDetails = ref<{
		photos: HTMLElement | null
		details: HTMLElement | null
		tripadvisor: { root: HTMLElement | null } | null
	}>()
	const visible = ref(false)
	const imgs = ref()
	const index = ref()
	const toast = useToast()
	const mapView = ref(false)
	const openModalPaid = ref(false)
	const googleKey = import.meta.env.DEV ? '' : import.meta.env.VITE_GOOGLE_KEY
	const fixedTop = ref(false)
	const loadButton = ref(false)
	const { width: pageWidth } = useWindowSize()
	const { form } = useHotels()
	const { invalidateQuery } = useInvalidateQuery()

	setTravelers(travelers.value)

	const tabs = ref<DetailsTab[]>([]) as Ref<DetailsTab[]>

	// Las pestañas dejan cada sección 170px bajo el borde superior; 10px de holgura.
	const tabOffset = 180
	// Pestaña elegida con clic: se mantiene mientras dura su scroll, porque la del
	// final de la página puede no alcanzar el offset y se marcaría otra.
	let pinnedTab = -1
	let releasePinnedTab: ReturnType<typeof setTimeout> | undefined

	const setCurrentTab = (index: number) => {
		tabs.value.forEach((tab, i) => (tab.current = i === index))
	}

	const holdPinnedTab = () => {
		clearTimeout(releasePinnedTab)
		releasePinnedTab = setTimeout(() => (pinnedTab = -1), 150)
	}

	const selectTab = (index: number) => {
		pinnedTab = index
		setCurrentTab(index)
		holdPinnedTab()
	}

	const onScroll = (e: Event) => {
		const target = e.target as Document

		fixedTop.value = target.documentElement.scrollTop > 50

		if (pinnedTab >= 0) {
			holdPinnedTab()
			return
		}

		// Posiciones en vivo: las imágenes cargan después y cambian las alturas.
		const { scrollTop, scrollHeight } = target.documentElement
		const atPageBottom = window.innerHeight + scrollTop >= scrollHeight - 1
		let current = 0

		tabs.value.forEach((tab, i) => {
			const top = tab.scrollTo?.getBoundingClientRect().top
			if (top === undefined) return
			if (top <= tabOffset || (atPageBottom && top < window.innerHeight)) {
				current = i
			}
		})

		setCurrentTab(current)
	}

	const showMultiple = (i: number, imagesArray: Item[]) => {
		const imageSources = [] as string[]

		imagesArray.forEach((image) => {
			imageSources.push(image.url)
		})

		imgs.value = imageSources

		index.value = i
		visible.value = true
	}

	const handleHide = () => {
		visible.value = false
	}

	const message = new URL(location.href)
	const onCopy = () => {
		toast.success('Url copiado al portapapeles')
	}

	const openMap = () => {
		mapView.value = true
		window.scrollTo(0, 0)
	}

	const carouselImages = computed(() => {
		const images = []

		if (hotel.value && hotel.value?.images.length > 0) {
			hotel.value.images.forEach((imageType) => {
				imageType.items.forEach((image) => {
					images.push(image.url)
				})
			})
		} else {
			images.push('http://0.0.0.0:5173/resources/images/logo-png-eb.png')
		}

		return images
	})

	const mediaQueries = computed(() => {
		return {
			isMobile: pageWidth.value < 1024,
			isDesktop: pageWidth.value >= 1024,
		}
	})

	const haveAttractions = computed(() => {
		return hotel.value.attractions.length > 0
	})

	const attractions = computed(() => {
		let attractions = [...hotel.value.attractions].filter((attraction) => {
			return attraction.refPoints.length > 0
		})

		attractions = attractions.filter(
			(attraction) =>
				attraction !== undefined && attraction.refPoints[0].distance !== 0
		)

		attractions = attractions
			.sort((a, b) => {
				return a.refPoints[0].distance - b.refPoints[0].distance
			})
			.slice(0, 5)

		return attractions
	})

	const OrderConditions = {
		AscendingSort: 'ascending',
		DescendingSort: 'descending',
		LowerPriceAgreement: 'lowerPriceAgreement',
		Agreement: 'agreement',
		AgreementCompany: 'agreementCompany',
	} as const

	const orderBy = ref(OrderConditions.AscendingSort)

	const orderByBeds = ref('all')

	const expandRooms = ref(false)

	const toggleExpandRooms = () => {
		if (expandRooms.value) {
			scrollToTargetAdjusted(rate.value)

			setTimeout(() => {
				expandRooms.value = !expandRooms.value
			}, 600)
		} else {
			expandRooms.value = !expandRooms.value
		}
	}

	const rooms = computed(() => hotel.value.rooms ?? [])

	const { roomsList } = useRoomListFormatting({
		orderBy,
		orderByBeds,
		rooms: rooms,
	})

	const selectedRoomIndex = ref()

	const selectedRoom = computed(() => {
		return roomsList.value[selectedRoomIndex.value]
	})

	const modals = reactive({
		viewMoreDetails: {
			value: false,
			open: function (index: number) {
				selectedRoomIndex.value = index
				this.value = true
			},
			close: function () {
				this.value = false
			},
		},
		viewAllAmenities: {
			value: false,
			open: function () {
				this.value = true
			},
			close: function () {
				this.value = false
			},
		},
	})

	const generalAmenities = computed(() => {
		const amenities = [...hotel.value.amenities]
		const paidAmenities = new Set(
			hotel.value.facilityDetails?.filter((facility) => facility.hasFee).map((facility) => facility.name) ?? []
		)

		return {
			amenities,
			paidAmenities,
			empty: amenities.length < 1,
			someAmenities: amenities.slice(0, 7),
			remainAmenitiesAmount: amenities.length - amenities.slice(0, 7).length,
			exceedOfAmenities: amenities.length > 7,
		}
	})

	const hotelTexts = computed(() => {
		return getHotelTexts(hotel.value?.texts ?? []).hotelTexts
	})

	const hotelImages = computed<HotelImages>(() => {
		let availableCategories = Array<Category>()

		categories.forEach((description) => {
			availableCategories.push({
				...description,
				images: [],
			} as { code: number; description: string; images: Item[] })
		})
		;[...hotel.value.images].forEach((set) => {
			set.items.forEach((image: Item) => {
				if (image.category && image.url) {
					if (image.category == '7') {
						image = { ...image, category: '6' }
					}

					availableCategories[+image.category - 1].images.push(image)
				}
			})
		})

		availableCategories = availableCategories.filter((category) => {
			return category.images.length > 0
		})

		// Only room images
		let roomImages = Array<Item>()

		;[...availableCategories].forEach((category) => {
			if (category.code == 6) {
				roomImages = [...roomImages, ...category.images]
			}
		})

		const firstExteriorImage = availableCategories.find((category) => {
			return category.code == 1
		})?.images[0]

		firstExteriorImage ? roomImages.unshift(firstExteriorImage) : undefined

		// availableCategories - roomImages
		const remainingCategories = Array<Category>()

		;[...availableCategories].forEach((category) => {
			if (category.code != 6) {
				remainingCategories.push(category)
			}
		})

		// Random room images to show at top
		const randomRoomImages = []

		if (roomImages.length > 2) {
			randomRoomImages.push(roomImages[Math.floor(roomImages.length / 2)])
			randomRoomImages.push(roomImages[roomImages.length - 1])
		}

		// Categories
		const carouselStructure = {} as CarouselModels

		availableCategories.forEach((category) => {
			carouselStructure[category.code] = { value: 0 }
		})

		return {
			availableCategories,
			roomImages,
			remainingCategories,
			randomRoomImages,
			carouselStructure,
			firstExteriorImage,
		}
	})

	const showMoreImages = ref(false)

	const scrollToTargetAdjusted = (element: HTMLElement) => {
		const headerOffset = 170
		const elementPosition = element.getBoundingClientRect().top
		const offsetPosition = elementPosition + window.scrollY - headerOffset

		window.scrollTo({
			top: offsetPosition,
			left: 0,
			behavior: 'smooth',
		})
	}

	const toggleMoreImages = function () {
		if (showMoreImages.value) {
			setTimeout(() => {
				const { photos } = photosAndDetails.value || {}
				if (photos) scrollToTargetAdjusted(photos)
			}, 100)
		}

		showMoreImages.value = !showMoreImages.value
	}

	const carouselModels = ref({})

	watch(
		() => hotelImages.value.carouselStructure,
		(value) => {
			carouselModels.value = value
		},
		{
			immediate: true,
			deep: true,
		}
	)

	watch(
		hotel,
		(value) => {
			if (value?.id) {
				setTimeout(function () {
					const hotelDetailsElement = hotelDetails.value?.hotelDetails

					if (hotelDetailsElement) {
						window.addEventListener('scroll', onScroll)
					}
				}, 1000)
			}
		},
		{ immediate: true }
	)

	watchEffect(() => {
		const list: Omit<DetailsTab, 'onSelect'>[] = [
			{
				name: 'Descripción general',
				scrollTo: hotelDetails.value?.hotelDetails ?? null,
				current: true,
			},
			{ name: 'Tarifas', scrollTo: rate.value, current: false },
			{
				name: 'Fotografías',
				scrollTo: photosAndDetails.value?.photos,
				current: false,
			},
			{
				name: 'Detalles',
				scrollTo: photosAndDetails.value?.details,
				current: false,
			},
			...(hotel.value?.tripadvisorHotelId ?
				[
					{
						name: 'Tripadvisor',
						scrollTo: photosAndDetails.value?.tripadvisor?.root ?? null,
						current: false,
						brand: 'tripadvisor' as const,
					},
				]
			:	[]),
		]
		tabs.value = list.map((tab, i) => ({
			...tab,
			onSelect: () => selectTab(i),
		}))
	})

	onBeforeUnmount(() => {
		window.removeEventListener('scroll', onScroll)
		clearTimeout(releasePinnedTab)
	})

	onMounted(() => {
		invalidateQuery(['hotel'])
	})

	return {
		hotel,
		isLoading,
		isError,
		isFetching,
		rate,
		hotelDetails,
		photosAndDetails,
		visible,
		imgs,
		index,
		toast,
		message,
		mapView,
		googleKey,
		fixedTop,
		loadButton,
		tabs,
		mediaQueries,
		haveAttractions,
		attractions,
		OrderConditions,
		orderBy,
		expandRooms,
		roomsList,
		selectedRoomIndex,
		selectedRoom,
		modals,
		generalAmenities,
		hotelTexts,
		carouselImages,
		hotelImages,
		carouselModels,
		showMoreImages,
		openModalPaid,
		travelers,
		form,
		onCopy,
		showMultiple,
		handleHide,
		openMap,
		toggleMoreImages,
		toggleExpandRooms,
		orderByBeds,
	}
}

export default useHotelDetails
